import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { OptimizedImage } from './ui/OptimizedImage';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const isDarkHeroPage = ['/', '/about', '/companies'].includes(pathname);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Companies', path: '/companies', hasDropdown: true },
    { name: 'Contact', path: '/contact' },
  ];

  const subCompanies = [
    { name: 'Kalyan Agencies', path: '/kalyan-agencies' },
    { name: 'Frostella Pickles', path: '/frostella-pickles' },
    { name: 'Frostella Desserts', path: '/frostella-desserts' },
    { name: 'Prime Learn Academy', path: '/prime-learn-academy' },
    { name: 'Lab @ Home', path: '/labathome' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
          scrolled ? "py-2" : "py-4",
          !scrolled && isDarkHeroPage ? "text-white" : "text-primary"
        )}
      >
        <div className="max-w-[95%] mx-auto">
          <div className={cn(
            "relative flex items-center justify-between px-3 h-14 rounded-full transition-all duration-500",
            scrolled
              ? "bg-white/90 backdrop-blur-md border border-white/20 shadow-soft"
              : "bg-transparent border-transparent"
          )}>

            {/* Logo */}
            <Link to="/" className="relative z-10 flex items-center gap-3 group">
              <OptimizedImage
                src="/images/millennium-logo.webp"
                alt="Millennium Group"
                priority={true}
                className={cn(
                  "w-8 h-8 object-contain transition-transform duration-300 group-hover:scale-105",
                  !scrolled && isDarkHeroPage && "brightness-0 invert"
                )}
              />
              <div className="flex flex-col">
                <span className={cn(
                  "font-display font-bold text-lg tracking-tight leading-none",
                  !scrolled && isDarkHeroPage ? "text-white" : "text-primary"
                )}>
                  Millennium Group
                </span>
                <span className={cn(
                  "text-[8px] sm:text-[9px] font-medium tracking-wide uppercase mt-0.5 transition-colors duration-500",
                  !scrolled && isDarkHeroPage ? "text-white/60" : "text-neutral-500"
                )}>
                  Events, Education, Food, Healthcare & More
                </span>
              </div>
            </Link>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-2">
              {navLinks.filter(l => l.name !== 'Home').map((link) => (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => link.hasDropdown && setIsDropdownOpen(true)}
                  onMouseLeave={() => link.hasDropdown && setIsDropdownOpen(false)}
                >
                  <Link
                    to={link.path}
                    className={cn(
                      "relative px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors duration-300 flex items-center gap-1",
                      pathname === link.path || (link.hasDropdown && subCompanies.some(sc => sc.path === pathname))
                        ? (scrolled || !isDarkHeroPage ? "text-primary bg-neutral-100" : "text-white bg-white/10")
                        : (scrolled || !isDarkHeroPage ? "text-neutral-500 hover:text-primary hover:bg-neutral-50" : "text-white/70 hover:text-white hover:bg-white/10")
                    )}
                  >
                    {link.name}
                    {link.hasDropdown && <ChevronDown className={cn("w-3 h-3 transition-transform duration-300", isDropdownOpen && "rotate-180")} />}
                  </Link>

                  {link.hasDropdown && (
                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-neutral-100 overflow-hidden py-2"
                        >
                          {subCompanies.map((sub) => (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={cn(
                                "block px-5 py-3 text-xs font-semibold uppercase tracking-wider transition-colors",
                                pathname === sub.path
                                  ? "text-primary bg-neutral-50"
                                  : "text-neutral-500 hover:text-primary hover:bg-neutral-50"
                              )}
                              onClick={() => setIsDropdownOpen(false)}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}

              <div className="w-px h-4 bg-neutral-200 mx-3" />

              <Link
                to="/companies"
                className={cn(
                  "group flex items-center gap-2 pl-4 pr-2 py-2 text-xs font-bold uppercase tracking-wider transition-colors",
                  !scrolled && isDarkHeroPage ? "text-white" : "text-primary"
                )}
              >
                Explore
                <span className="w-6 h-6 rounded-full bg-accent text-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-md shadow-accent/20">
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              className={cn(
                "md:hidden relative z-10 p-2 transition-colors",
                !scrolled && isDarkHeroPage ? "text-white" : "text-primary"
              )}
              onClick={() => setIsOpen(true)}
              aria-label="Open Menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-xl"
          >
            <div className="flex flex-col h-full p-6 md:p-10">

              {/* Header */}
              <div className="flex justify-between items-center mb-8">
                <div className="flex flex-col">
                  <span className="font-display font-bold text-lg tracking-tight text-primary leading-none">Millennium Group</span>
                  <span className="text-[10px] text-neutral-500 font-medium tracking-wide uppercase mt-1">
                    Events, Education, Food, Healthcare & More
                  </span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-3 bg-neutral-100 rounded-full text-primary hover:bg-neutral-200 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Links */}
              <div className="flex flex-col gap-6 overflow-y-auto">
                {navLinks.filter(link => link.name !== 'Contact').map((link, i) => (
                  <div key={link.path}>
                    <motion.div
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 + (i * 0.05) }}
                      className="group"
                    >
                      <Link
                        to={link.path}
                        className="flex items-end gap-4"
                        onClick={() => setIsOpen(false)}
                      >
                        <span className="text-xs font-bold text-neutral-300 mb-2 font-mono">0{i + 1}</span>
                        <span className="text-5xl font-display font-bold text-primary group-hover:text-accent transition-colors">
                          {link.name}
                        </span>
                      </Link>
                    </motion.div>

                    {/* Sub-menu for Companies */}
                    {link.hasDropdown && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ delay: 0.3 }}
                        className="mt-4 ml-10 flex flex-col gap-3 pl-4 border-l border-neutral-200"
                      >
                        {subCompanies.map((sub, j) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className="text-lg font-medium text-neutral-500 hover:text-primary transition-colors block py-1"
                            onClick={() => setIsOpen(false)}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer CTA */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-auto border-t border-neutral-200 pt-8"
              >
                <Link
                  to="/contact"
                  className="w-full flex items-center justify-between p-4 bg-primary text-white rounded-2xl group"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="flex flex-col">
                    <span className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-1">Have a project?</span>
                    <span className="text-xl font-bold">Let's Talk</span>
                  </div>
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-colors">
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </Link>

                <div className="flex flex-col gap-4 mt-8 text-neutral-400 text-xs font-medium uppercase tracking-wider">
                  <div className="flex justify-center gap-6">
                    <a href="https://instagram.com/millennium_group_of_companies" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Instagram</a>
                    <a href="https://www.facebook.com/people/Millennium-Group-Of-Companies/61587052833791/" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Facebook</a>
                    <a href="https://wa.me/917025865774" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">WhatsApp</a>
                  </div>
                  <div className="text-center opacity-60">© 2026 Millennium</div>
                </div>
              </motion.div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
